import { useState, useRef } from "react";
import { Upload, X, RefreshCw, AlertCircle, Image as ImageIcon, MapPin, Check } from "lucide-react";
import { api, ApiError, resolveImageUrl } from "../../lib/api";

export interface QuestionFigureLocation {
  source: string;
  pdfPage: number;
  region?: string;
  label?: string;
  description?: string;
  bbox?: number[];
}

export interface QuestionFigureUploaderProps {
  token: string;
  figureStatus?: "none" | "required" | "uploaded";
  figureImage?: string | null;
  figureAlt?: string | null;
  figureLocation?: QuestionFigureLocation | null;
  onChange: (data: {
    figure_status: "none" | "required" | "uploaded";
    figure_image: string | null;
    figure_alt: string | null;
    figure_location?: QuestionFigureLocation | null;
  }) => void;
  disabled?: boolean;
}

export function QuestionFigureUploader({
  token,
  figureStatus = "none",
  figureImage = null,
  figureAlt = "",
  figureLocation = null,
  onChange,
  disabled = false,
}: QuestionFigureUploaderProps) {
  const [alt, setAlt] = useState(figureAlt || "");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resolvedUrl = resolveImageUrl(figureImage);

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input
    e.target.value = "";

    // Validate size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError("File exceeds 5 MB limit. Please select a smaller image.");
      return;
    }

    // Validate extension
    const name = file.name.toLowerCase();
    if (!name.endsWith(".png") && !name.endsWith(".jpg") && !name.endsWith(".jpeg") && !name.endsWith(".webp")) {
      setError("Only PNG, JPG, and WebP images are allowed.");
      return;
    }

    const currentAlt = alt.trim() || figureAlt?.trim() || "Question illustration";

    setUploading(true);
    setError("");
    try {
      const res = await api.admin.uploadQuestionFigure(token, file, currentAlt);
      setAlt(res.figure_alt);
      onChange({
        figure_status: "uploaded",
        figure_image: res.figure_image,
        figure_alt: res.figure_alt,
        figure_location: figureLocation,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  function handleAltChange(newAlt: string) {
    setAlt(newAlt);
    if (figureImage) {
      onChange({
        figure_status: "uploaded",
        figure_image: figureImage,
        figure_alt: newAlt,
        figure_location: figureLocation,
      });
    }
  }

  function handleRemove() {
    onChange({
      figure_status: figureLocation ? "required" : "none",
      figure_image: null,
      figure_alt: null,
      figure_location: figureLocation,
    });
    setAlt("");
    setError("");
  }

  function handleMarkNotNeeded() {
    onChange({
      figure_status: "none",
      figure_image: null,
      figure_alt: null,
      figure_location: figureLocation,
    });
    setAlt("");
    setError("");
  }

  return (
    <div className="space-y-2.5 rounded-lg border border-border bg-card/60 p-3">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-foreground flex items-center gap-1.5">
          <ImageIcon size={14} className="text-primary" />
          Question Figure
          {figureStatus === "required" && !figureImage && (
            <span className="rounded bg-warning/10 px-1.5 py-0.5 text-xs font-semibold text-warning border border-warning/30">
              Figure required
            </span>
          )}
          {figureStatus === "uploaded" && figureImage && (
            <span className="rounded bg-success/10 px-1.5 py-0.5 text-xs font-semibold text-success border border-success/30">
              Uploaded
            </span>
          )}
          {figureStatus === "none" && !figureImage && (
            <span className="text-muted-foreground text-xs font-normal">
              (Optional)
            </span>
          )}
        </span>

        {figureStatus === "required" && !figureImage && (
          <button
            type="button"
            onClick={handleMarkNotNeeded}
            disabled={disabled}
            className="text-xs text-muted-foreground hover:text-foreground underline decoration-dotted transition"
          >
            No figure needed
          </button>
        )}
      </div>

      {/* Location hint from extraction/import if available */}
      {figureLocation && (
        <div className="flex items-start gap-1.5 rounded-md bg-secondary/60 p-2 text-xs text-muted-foreground border border-border/50">
          <MapPin size={13} className="shrink-0 text-primary mt-0.5" />
          <div>
            <span className="font-medium text-foreground">Location hint:</span>{" "}
            {figureLocation.source ? `${figureLocation.source} · ` : ""}
            page {figureLocation.pdfPage}
            {figureLocation.region ? ` · ${figureLocation.region}` : ""}
            {figureLocation.label ? ` · ${figureLocation.label}` : ""}
            {figureLocation.description ? ` (${figureLocation.description})` : ""}
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-1.5 rounded-md bg-destructive/10 p-2 text-xs text-destructive border border-destructive/30">
          <AlertCircle size={13} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Uploaded image preview and controls */}
      {figureImage && resolvedUrl ? (
        <div className="space-y-2">
          <div className="relative group overflow-hidden rounded-md border border-border bg-black/5 dark:bg-black/20 flex items-center justify-center max-h-48 p-2">
            <img
              src={resolvedUrl}
              alt={alt || "Question figure"}
              className="max-h-44 w-auto max-w-full rounded object-contain"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">
              Alt text (accessibility description) <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={alt}
              onChange={(e) => handleAltChange(e.target.value)}
              disabled={disabled}
              placeholder="e.g. Diagram showing a circuit with resistor and capacitor"
              className="w-full text-xs rounded-md border border-border bg-background px-2.5 py-1.5 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.webp"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || disabled}
              className="px-2.5 py-1 text-xs font-medium rounded border border-border bg-card hover:bg-muted text-foreground transition flex items-center gap-1.5"
            >
              <RefreshCw size={12} className={uploading ? "animate-spin" : ""} />
              {uploading ? "Replacing..." : "Replace"}
            </button>
            <button
              type="button"
              onClick={handleRemove}
              disabled={uploading || disabled}
              className="px-2.5 py-1 text-xs font-medium rounded border border-border bg-card hover:bg-destructive/10 text-destructive hover:border-destructive/30 transition flex items-center gap-1.5"
            >
              <X size={12} />
              Remove
            </button>
          </div>
        </div>
      ) : (
        /* Empty / upload dropzone */
        <div className="space-y-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">
              Alt text (describe what the figure illustrates) <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              disabled={disabled}
              placeholder="e.g. Diagram of anatomical heart structure with labels"
              className="w-full text-xs rounded-md border border-border bg-background px-2.5 py-1.5 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,.webp"
            onChange={handleFileSelect}
            className="hidden"
          />

          <button
            type="button"
            onClick={() => {
              if (!alt.trim()) {
                setError("Please enter alt text before selecting an image.");
                return;
              }
              fileInputRef.current?.click();
            }}
            disabled={uploading || disabled}
            className="w-full border-2 border-dashed border-border hover:border-primary/50 hover:bg-primary/5 rounded-md p-3 text-center transition flex flex-col items-center justify-center gap-1 cursor-pointer disabled:opacity-50"
          >
            <Upload size={18} className="text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {uploading ? "Processing figure..." : "Upload question figure"}
            </span>
            <span className="text-xs text-muted-foreground">
              PNG, JPG, or WebP up to 5 MB
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
